<template>
  <div
    class="two-drawer-layout__screen-height--after-secondary-subheader tw-p-6"
  >
    <div
      class="
        tw-rounded-lg
        tw-h-full
        tw-max-w-condensed-content
        tw-mx-auto
        tw-flex
        tw-flex-row
        tw-w-full
      "
      :class="borderClasses"
    >
      <SfAssistantChecksEmpty v-if="!hasChecks && !loading" />
      <template v-else>
        <div
          class="
            tw-h-full
            tw-shrink-0
            tw-grow
            tw-rounded-l-lg
            tw-p-4
            tw-w-condensed-sidepane
            tw-max-w-condensed-sidepane
            tw-overflow-y-auto
            tw-bg-sf-neutral-subtle-1
          "
        >
          <div v-if="loading">
            <SfSkeletonLoader
              v-for="i in 15"
              :key="i"
              height="2rem"
              :width="['10rem', '15rem', '20rem'][i % 3]"
              class="tw-px-2"
            />
          </div>
          <SfAssistantChecksLeftSidepane
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
            @select="setSelected"
          >
          </SfAssistantChecksLeftSidepane>
        </div>
        <div
          data-testid="assistant-checks-container"
          class="
            tw-h-full
            tw-shrink
            tw-grow
            tw-overflow-y-auto
            tw-rounded-r-lg
            tw-bg-sf-snow-lighter
          "
        >
          <div
            v-if="dataStatus === 'READY_FOR_RELOAD'"
            class="
              tw-sticky tw-top-0 tw-z-50 tw-bg-sf-snow-lighter tw-px-6 tw-py-4
            "
          >
            <sf-banner-inline variant="info" class="tw-block">
              <template #title>
                <span class="tw-font-semibold tw-mr-1">
                  {{
                    I18n.t(
                      'assistant_check.page.reload_stale_data_banner.title'
                    )
                  }}
                </span>
                <SfButtonInverted
                  :small="true"
                  :inline="true"
                  @click="reloadAfterCalculation"
                >
                  {{
                    I18n.t(
                      'assistant_check.page.reload_stale_data_banner.reload_button'
                    )
                  }}
                </SfButtonInverted>
              </template>
            </sf-banner-inline>
          </div>
          <div
            v-else
            class="
              tw-sticky tw-top-0 tw-z-50 tw-bg-sf-snow-lighter tw-px-6 tw-pt-4
            "
          />
          <div v-if="loading">
            <RemarkV2Skeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
          </div>
          <SfAssistantChecksContainer
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'

import { filteredCategory } from '../constants'
import { ChecksCalculationStatus } from '../types'
import RemarkV2Skeleton from '@app/shared/vue/components/molecules/remark-v2/remark-v2-skeleton.vue'
import SfAssistantChecksContainer from '@app/application/sf-assistant-checks/components/sf-assistant-checks-container.vue'
import SfAssistantChecksEmpty from '@app/application/sf-assistant-checks/components/sf-assistant-checks-empty.vue'
import SfAssistantChecksLeftSidepane from '@app/application/sf-assistant-checks/components/sf-assistant-checks-left-sidepane.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import {
  ALL_CHECKS,
  UNREAD_CHECKS,
} from '@app/application/sf-assistant-checks/utils'
import { useSfAssistantChecksPage } from '../stores/page'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import Polling from '@app/utils/polling'
import useI18n from '@app/shared/vue/composables/use-I18n'
import useUrlStorage from '@app/shared/vue/composables/use-url-storage'

const I18n = useI18n()

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfAssistantChecksPage',
})

interface Props {
  contextId: string | undefined
  initialCategory?: string
  initialItem?: string
}
const props = withDefaults(defineProps<Props>(), {
  initialCategory: UNREAD_CHECKS,
  initialItem: '',
})

const sfAssistantChecksPageStore = useSfAssistantChecksPage()
const {
  dataStatus,
  filteredUnreadCheckCount,
  loading,
  hasChecks,
  isSearchQueryPresent,
  receivedInitialSearchQuery,
} = storeToRefs(sfAssistantChecksPageStore)

if (props.contextId) {
  sfAssistantChecksPageStore.setWorkflowId(parseInt(props.contextId, 10))
}

sfAssistantChecksPageStore.fetchAll().then(() => {
  const triggerCalculation = sfAssistantChecksPageStore.triggerCalculation
  if (triggerCalculation.stale) {
    dataStatus.value = ChecksCalculationStatus.Calculating
  }

  const polling = new Polling(
    triggerCalculation.channel,
    triggerCalculation.lastId,
    () => {
      if (hasChecks.value) {
        dataStatus.value = ChecksCalculationStatus.ReadyForReload
      } else {
        reloadAfterCalculation()
      }
    }
  )
  polling.subscribe()
})

function reloadAfterCalculation(): void {
  sfAssistantChecksPageStore.fetchAll()
  dataStatus.value = ChecksCalculationStatus.UpToDate
}

const selectedCategory = useUrlStorage(
  'category',
  props.initialCategory,
  (val) => val
)
const selectedItem = useUrlStorage('item', props.initialItem, (val) => val)

const borderClasses = computed(() => {
  return !hasChecks.value && !loading.value
    ? []
    : ['tw-border', 'tw-border-solid', 'tw-border-sf-neutral-lighter-2']
})

const setSelected = (category: string, item: string): void => {
  selectedCategory.value = category
  selectedItem.value = item
}

watchEffect(() => {
  if (loading.value) {
    return
  }

  if (
    selectedCategory.value === UNREAD_CHECKS &&
    filteredUnreadCheckCount.value === 0
  ) {
    setSelected(ALL_CHECKS, '')
  }
})

watch(isSearchQueryPresent, () => {
  if (receivedInitialSearchQuery.value) {
    if (isSearchQueryPresent.value) {
      setSelected(filteredCategory, '')
    } else {
      if (selectedCategory.value === filteredCategory) {
        setSelected(ALL_CHECKS, '')
      }
    }
  }
})
</script>
